import {useCallback, useEffect, useMemo, useState} from "react";
import {Button, DatePicker, Input, message, Select, Spin, Switch} from "antd";
import {ArrowLeft, Building2, CalendarDays, Fingerprint, KeyRound, RotateCcw, Save, Smartphone} from "lucide-react";
import dayjs from "dayjs";
import {Navigate, useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {APIv1} from "../../api";
import {getStoredUser} from "../../utils";
import {withPrefilledOption} from "../../utils/device/deviceUtils";
import "../../forms/AddNewDeviceForm/AddNewDeviceForm.scss";
import {playFeedbackWave} from "../../utils/feedbackWave";

const DATE_FORMAT = "YYYY-MM-DD";

const initialFormData = {
    company: null,
    company_label: "",
    company_inn: "",
    device: null,
    device_label: "",
    device_serial_number: "",
    license_device_id: "",
    secret_key: "",
    is_active: false,
    subscription_end_date: null,
};

const Field = ({label, required, icon, children, wide}) => (
    <div className={`create-device__field${wide ? " create-device__field--wide" : ""}`}>
        <div className="create-device__field-label">
            {icon && <span className="create-device__field-icon">{icon}</span>}
            <span className={required ? "create-device__required-label" : ""}>{label}</span>
        </div>
        <div className="create-device__field-control">
            {children}
        </div>
    </div>
);

const getObjectId = (value) => {
    if (value === null || value === undefined || value === "") {
        return null;
    }

    if (typeof value === "object") {
        return value.id ?? null;
    }

    return value;
};

const getCompanyLabel = (license) => (
    license.company?.name ?? license.company_name ?? ""
);

const getDeviceLabel = (license) => (
    license.device?.device_serial_number ??
    license.device?.serial_number ??
    license.device_serial_number ??
    ""
);

const normalizeLicenseFormData = (license) => ({
    company: getObjectId(license.company ?? license.company_id),
    company_label: getCompanyLabel(license),
    company_inn: license.company?.inn ?? license.company_inn ?? "",
    device: getObjectId(license.device ?? license.device_id),
    device_label: getDeviceLabel(license),
    device_serial_number: getDeviceLabel(license),
    license_device_id: license.license_device_id ?? "",
    secret_key: license.secret_key ?? "",
    is_active: Boolean(license.is_active),
    subscription_end_date: license.subscription_end_date ?? null,
});

const normalizeComparableValue = (value) => {
    if (value === undefined) {
        return null;
    }

    return value;
};

const getChangedPayload = (formData, initialData) => {
    const fields = [
        "company",
        "device",
        "license_device_id",
        "secret_key",
        "is_active",
        "subscription_end_date",
    ];

    return fields.reduce((payload, field) => {
        if (normalizeComparableValue(formData[field]) !== normalizeComparableValue(initialData[field])) {
            payload[field] = formData[field];
        }

        return payload;
    }, {});
};

const getCompanyOption = (company) => ({
    value: company.id,
    label: company.name,
    inn: company.inn,
});

const getDeviceOption = (device) => ({
    value: device.device_id ?? device.device?.id ?? device.id,
    label: device.device_serial_number ?? device.serial_number ?? device.device_serial ?? `Device ${device.id}`,
});

const MarkerMobileUpdate = () => {
    const {id} = useParams();
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [userData] = useState(() => getStoredUser() || {});
    const [formData, setFormData] = useState(initialFormData);
    const [initialData, setInitialData] = useState(initialFormData);
    const [companies, setCompanies] = useState([]);
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectLoading, setSelectLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const isStaff = Boolean(userData?.data?.is_admin);

    const authHeaders = useMemo(() => ({
        Authorization: `Token ${userData.token}`,
    }), [userData.token]);

    const updateField = (field, value) => {
        setFormData((current) => ({
            ...current,
            [field]: value,
        }));
    };

    const fetchLicense = useCallback(async () => {
        if (!userData.token || !id || !isStaff) {
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const {data} = await APIv1.get(`/device/license/detail/${id}/`, {
                headers: authHeaders,
            });
            const normalizedData = normalizeLicenseFormData(data);

            setFormData(normalizedData);
            setInitialData(normalizedData);
        } catch (error) {
            console.error("Error fetching marker mobile license:", error);
            message.error(t("pages.markerMobile.update.messages.fetchError"));
        } finally {
            setLoading(false);
        }
    }, [authHeaders, id, isStaff, t, userData.token]);

    useEffect(() => {
        fetchLicense();
    }, [fetchLicense]);

    const fetchCompanies = async () => {
        if (!userData.token || companies.length > 0) return;

        setSelectLoading(true);

        try {
            const {data} = await APIv1.get("/company/list/", {
                params: {
                    page_size: 100,
                },
                headers: authHeaders,
            });
            const results = data?.results ?? data ?? [];

            setCompanies(results.map(getCompanyOption));
        } catch (error) {
            console.error("Error fetching companies:", error);
            message.error(t("pages.markerMobile.update.messages.fetchCompaniesError"));
        } finally {
            setSelectLoading(false);
        }
    };

    const fetchDevices = async () => {
        if (!userData.token || devices.length > 0) return;

        setSelectLoading(true);

        try {
            const {data} = await APIv1.get("/device/list/", {
                params: {
                    page_size: 100,
                },
                headers: authHeaders,
            });
            const results = data?.results ?? data ?? [];

            setDevices(results.map(getDeviceOption).filter((device) => device.value !== null && device.value !== undefined));
        } catch (error) {
            console.error("Error fetching devices:", error);
            message.error(t("pages.markerMobile.update.messages.fetchDevicesError"));
        } finally {
            setSelectLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!formData.license_device_id.trim()) {
            message.warning(t("pages.markerMobile.update.messages.licenseRequired"));
            return;
        }

        const payload = getChangedPayload(formData, initialData);

        if (Object.keys(payload).length === 0) {
            message.info(t("pages.markerMobile.update.messages.noChanges"));
            return;
        }

        setSubmitting(true);

        try {
            await APIv1.patch(`/device/license/update/${id}/`, payload, {
                headers: authHeaders,
            });
            playFeedbackWave('success');
            message.success(t("pages.markerMobile.update.messages.updateSuccess"));
            navigate(`/devices/marker-mobile/detail/${id}`);
        } catch (error) {
            console.error("Error updating marker mobile license:", error);
            const firstMessage = Object.values(error?.response?.data ?? {})?.flat?.()?.[0];
            playFeedbackWave('failure');
            message.error(firstMessage || t("pages.markerMobile.update.messages.updateError"));
        } finally {
            setSubmitting(false);
        }
    };

    const handleClear = () => {
        setFormData(initialData);
        message.info(t("pages.markerMobile.update.messages.changesDiscarded"));
    };

    if (userData.token && !isStaff) {
        return <Navigate to="/analysis" replace/>;
    }

    if (loading) {
        return (
            <div className="content_container create-device">
                <Spin/>
            </div>
        );
    }

    return (
        <div className="content_container create-device">
            <div className="create-device__header">
                <div className="create-device__heading">
                    <span className="create-device__eyebrow">{t("pages.markerMobile.update.eyebrow")}</span>
                    <h1>{t("pages.markerMobile.update.title")}</h1>
                    <span className="create-device__serial">#{id}</span>
                </div>
                <Button
                    className="create-device__action-button create-device__action-button--secondary"
                    icon={<ArrowLeft size={16}/>}
                    onClick={() => navigate(-1)}
                >
                    {t("common.backBtn")}
                </Button>
            </div>

            <div className="create-device__forms">
                <section className="create-device__forms-container">
                    <div className="create-device__section-header">
                        <span className="create-device__section-index">01</span>
                        <h2 className="create-device__forms-title">{t("pages.markerMobile.detail.sections.licenseInformation")}</h2>
                    </div>

                    <div className="create-device__field-grid">
                        <Field label={t("pages.markerMobile.listColumns.company")} icon={<Building2 size={16}/>}>
                            <Select
                                className="create-device__control"
                                placeholder={t("pages.markerMobile.update.placeholders.company")}
                                value={formData.company ?? undefined}
                                onChange={(value, option) => {
                                    updateField("company", value);
                                    updateField("company_label", option?.label ?? "");
                                    updateField("company_inn", option?.inn ?? "");
                                }}
                                options={withPrefilledOption(companies, formData.company, formData.company_label)}
                                optionLabelProp="label"
                                showSearch
                                virtual={false}
                                dropdownMatchSelectWidth={false}
                                filterOption={(input, option) =>
                                    option?.label?.toLowerCase().includes(input.toLowerCase())
                                }
                                loading={selectLoading}
                                onDropdownVisibleChange={(open) => {
                                    if (open) fetchCompanies();
                                }}
                            />
                        </Field>

                        <Field label={t("pages.markerMobile.update.fields.device")} icon={<Smartphone size={16}/>}>
                            <Select
                                className="create-device__control"
                                placeholder={t("pages.markerMobile.update.placeholders.device")}
                                value={formData.device ?? undefined}
                                onChange={(value, option) => {
                                    updateField("device", value);
                                    updateField("device_label", option?.label ?? "");
                                }}
                                options={withPrefilledOption(devices, formData.device, formData.device_label)}
                                optionLabelProp="label"
                                showSearch
                                virtual={false}
                                dropdownMatchSelectWidth={false}
                                filterOption={(input, option) =>
                                    option?.label?.toLowerCase().includes(input.toLowerCase())
                                }
                                loading={selectLoading}
                                onDropdownVisibleChange={(open) => {
                                    if (open) fetchDevices();
                                }}
                            />
                        </Field>

                        <Field label={t("pages.markerMobile.listColumns.licenseDeviceId")} required icon={<Fingerprint size={16}/>}>
                            <Input
                                className="create-device__control"
                                allowClear
                                value={formData.license_device_id}
                                onChange={(event) => updateField("license_device_id", event.target.value)}
                            />
                        </Field>

                        <Field label={t("pages.markerMobile.update.fields.secretKey")} icon={<KeyRound size={16}/>}>
                            <Input
                                className="create-device__control"
                                allowClear
                                value={formData.secret_key}
                                onChange={(event) => updateField("secret_key", event.target.value)}
                            />
                        </Field>

                        <Field label={t("pages.markerMobile.update.fields.subscriptionEndDate")} icon={<CalendarDays size={16}/>}>
                            <DatePicker
                                className="create-device__control"
                                format={DATE_FORMAT}
                                allowClear
                                value={formData.subscription_end_date ? dayjs(formData.subscription_end_date, DATE_FORMAT) : null}
                                onChange={(date) => updateField("subscription_end_date", date ? date.format(DATE_FORMAT) : null)}
                            />
                        </Field>

                        <Field label={t("common.active")}>
                            <Switch
                                checked={formData.is_active}
                                onChange={(checked) => updateField("is_active", checked)}
                            />
                        </Field>
                    </div>
                </section>
            </div>

            <div className="create-device__footer">
                <span className="create-device__footer-text">
                    {t("pages.markerMobile.update.footer", {id})}
                </span>
                <div className="create-device__footer-actions">
                    <Button
                        className="create-device__action-button create-device__action-button--secondary"
                        icon={<RotateCcw size={16}/>}
                        onClick={handleClear}
                    >
                        {t("common.clearBtn")}
                    </Button>
                    <Button
                        className="create-device__action-button create-device__action-button--primary"
                        icon={<Save size={16}/>}
                        loading={submitting}
                        onClick={handleSubmit}
                    >
                        {t("common.submitBtn")}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default MarkerMobileUpdate;
